import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Phone, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";

export const Hero = () => {
  return (
    <section
      id="top"
      className="relative overflow-hidden border-b border-border bg-background"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 bg-grid opacity-60" aria-hidden />
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-secondary/60 to-transparent" aria-hidden />

      <div className="container relative mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-24 md:grid-cols-12 md:gap-8 md:px-12 md:py-32 lg:py-36">
        <div className="md:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-6 inline-flex items-center gap-2 border border-border bg-white px-3 py-1.5"
          >
            <span className="h-1.5 w-1.5 bg-primary" />
            <span className="font-[Outfit] text-xs font-bold uppercase tracking-[0.2em] text-foreground/70">
              Tumakuru&apos;s Trusted CA Firm
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="text-5xl font-extrabold leading-[0.95] tracking-tight text-foreground md:text-6xl lg:text-[4.5rem]"
            data-testid="hero-heading"
          >
            Precision
            <span className="mx-3 inline-block h-3 w-3 translate-y-[-8px] bg-primary md:h-4 md:w-4" />
            Tax<br />
            & Compliance for<br />
            <span className="italic text-primary">your business.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg"
          >
            NP Associates is a chartered accountancy firm based in Tumakuru,
            helping businesses and individuals with GST, ITR filing and end-to-end
            tax compliance — at genuinely affordable fees.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Button
              asChild
              size="lg"
              className="group h-14 rounded-none bg-primary px-8 font-[Outfit] text-sm font-semibold uppercase tracking-wider hover:bg-primary/90"
              data-testid="hero-primary-cta"
            >
              <a href="#contact">
                Get Free Consultation
                <ArrowUpRight className="ml-2 h-4 w-4 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="h-14 rounded-none border-foreground bg-transparent px-8 font-[Outfit] text-sm font-semibold uppercase tracking-wider text-foreground hover:bg-foreground hover:text-background"
              data-testid="hero-secondary-cta"
            >
              <a href="tel:7353626623">
                <Phone className="mr-2 h-4 w-4" />
                Call 73536 26623
              </a>
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4 border-t border-border pt-8"
          >
            {[
              { k: "10+", v: "Years of Experience" },
              { k: "500+", v: "Happy Clients" },
              { k: "100%", v: "Compliance Rate" },
            ].map((s) => (
              <div key={s.v} className="flex items-baseline gap-3">
                <span className="font-[Outfit] text-3xl font-extrabold text-foreground">{s.k}</span>
                <span className="text-xs uppercase tracking-widest text-muted-foreground">{s.v}</span>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="relative md:col-span-5"
        >
          <div className="relative aspect-[4/5] overflow-hidden border border-border">
            <img
              src="https://images.unsplash.com/photo-1520607162513-77705c0f0d4a"
              alt="NP Associates professional workspace"
              className="h-full w-full object-cover"
              loading="eager"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground/40 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 border border-white/40 bg-white/95 p-5 backdrop-blur">
              <div className="flex items-start gap-3">
                <ShieldCheck className="h-5 w-5 flex-none text-primary" />
                <div>
                  <div className="font-[Outfit] text-sm font-bold uppercase tracking-wider text-foreground">
                    GST & Income Tax Auditors
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Trusted tax advisory for businesses across Karnataka.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute -bottom-4 -left-4 hidden h-24 w-24 border border-primary bg-primary md:block" />
          <div className="absolute -top-4 -right-4 hidden h-16 w-16 border border-foreground bg-foreground md:block" />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
